import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Check, Mail, Download } from 'lucide-react';
import ReceiptTemplate from '../components/ReceiptTemplate';
import { createReceipt as createReceiptUtil } from '../utils/receiptGenerator';
import { downloadReceiptPDF } from '../utils/pdfGenerator';
import { createReceipt } from '../utils/api';
import { getApiBaseUrl } from '../utils/apiBase';
import { SHOPPING_STOP_OPTIONS } from '../mock/fareData';

const TripCompletionForm = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('form'); // 'form', 'preview', 'success'
  const [receipt, setReceipt] = useState(null);
  const [loadingBooking, setLoadingBooking] = useState(false);
  const [bookingReference, setBookingReference] = useState('');
  const [bookingError, setBookingError] = useState('');
  
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    bookingReference: '',
    pickupLocation: '',
    dropoffLocation: '',
    date: new Date().toISOString().split('T')[0],
    dropoffTime: '',
    fareCollected: '',
    driverName: '',
    driverId: '',
    passengers: '',
    luggage: '',
    paymentMethod: 'cash',
    serviceType: '',
    notes: '',
    // LB-2: Multi-stop / round-trip support
    tripType: 'one-way',
    additionalStops: [],
    returnDate: '',
    returnTime: '',
    // Shopping Plaza Stop — single-select. Values: none|grocery|liquor|both
    shoppingStop: 'none'
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAddStop = () => {
    setFormData((prev) => ({ ...prev, additionalStops: [...(prev.additionalStops || []), ''] }));
  };

  const handleStopChange = (index, value) => {
    setFormData((prev) => {
      const next = [...(prev.additionalStops || [])];
      next[index] = value;
      return { ...prev, additionalStops: next };
    });
  };

  const handleRemoveStop = (index) => {
    setFormData((prev) => {
      const next = [...(prev.additionalStops || [])];
      next.splice(index, 1);
      return { ...prev, additionalStops: next };
    });
  };

  const handleLoadBooking = async () => {
    if (!bookingReference.trim()) {
      setBookingError('Please enter a booking reference number');
      return;
    }

    setLoadingBooking(true);
    setBookingError('');

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setBookingError('Authentication required. Please login again.');
        setLoadingBooking(false);
        return;
      }

      const response = await fetch(`${getApiBaseUrl()}/api/bookings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please login again.');
        }
        throw new Error('Failed to fetch bookings');
      }

      const bookings = await response.json();
      const booking = bookings.find(b => b.id === bookingReference.trim());

      if (!booking) {
        setBookingError('No booking found for this reference number.');
        setLoadingBooking(false);
        return;
      }

      // Auto-fill form with booking data
      setFormData({
        ...formData,
        customerName: booking.passengerName || '',
        customerEmail: booking.passengerEmail || '',
        customerPhone: booking.passengerPhone || '',
        bookingReference: booking.id,
        pickupLocation: booking.pickupLocation || '',
        dropoffLocation: booking.dropoffLocation || '',
        date: booking.date || formData.date,
        dropoffTime: booking.time || '',
        fareCollected: booking.actualFare || booking.estimatedFare || '',
        passengers: booking.passengers || '',
        luggage: booking.luggage || '',
        paymentMethod: booking.paymentMethod || 'cash',
        serviceType: booking.serviceType || '',
        notes: booking.specialRequests || booking.notes || ''
      });

      setBookingError('');
      alert('✅ Booking loaded successfully! Review and edit fields as needed.');
    } catch (error) {
      console.error('Error loading booking:', error);
      setBookingError('Failed to load booking. Please try again.');
    } finally {
      setLoadingBooking(false);
    }
  };

  const handlePreview = (e) => {
    e.preventDefault();
    const newReceipt = createReceiptUtil(formData);
    setReceipt(newReceipt);
    setStep('preview');
  };

  const handleConfirm = async () => {
    try {
      // Check if we have a booking reference (loaded booking workflow)
      if (formData.bookingReference) {
        // Call the real booking completion endpoint
        const result = await fetch(`${getApiBaseUrl()}/api/bookings/${formData.bookingReference}/complete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify({
            actual_fare: parseFloat(formData.fareCollected),
            payment_method: formData.paymentMethod || 'cash',
            notes: formData.notes || ''
          })
        });

        if (!result.ok) {
          const error = await result.json();
          throw new Error(error.detail || 'Failed to complete booking');
        }

        const completionData = await result.json();
        
        // Create receipt object for display
        const receiptForDisplay = {
          reference: completionData.booking.receiptNumber || formData.bookingReference,
          customerName: formData.customerName,
          customerEmail: formData.customerEmail,
          date: formData.date,
          dropoffTime: formData.dropoffTime,
          fareCollected: parseFloat(formData.fareCollected),
          driverName: formData.driverName,
          driverId: formData.driverId,
          pickupLocation: formData.pickupLocation,
          dropoffLocation: formData.dropoffLocation,
          shoppingStop: formData.shoppingStop || 'none'
        };

        setReceipt(receiptForDisplay);
        setStep('success');
        
        // Reset after 5 seconds
        setTimeout(() => {
          setStep('form');
          setFormData({
            customerName: '',
            customerEmail: '',
            customerPhone: '',
            bookingReference: '',
            pickupLocation: '',
            dropoffLocation: '',
            date: new Date().toISOString().split('T')[0],
            dropoffTime: '',
            fareCollected: '',
            driverName: '',
            driverId: '',
            passengers: '',
            luggage: '',
            paymentMethod: 'cash',
            serviceType: '',
            notes: '',
            tripType: 'one-way',
            additionalStops: [],
            returnDate: '',
            returnTime: '',
            shoppingStop: 'none'
          });
          setReceipt(null);
          setBookingReference('');
        }, 5000);
      } else {
        // Fallback: Manual standalone receipt creation (no booking reference)
        const cleanedStops = (formData.additionalStops || []).map((s) => (s || '').trim()).filter(Boolean);
        const receiptPayload = {
          customerName: formData.customerName,
          customerEmail: formData.customerEmail,
          pickupLocation: formData.pickupLocation,
          dropoffLocation: formData.dropoffLocation,
          date: formData.date,
          dropoffTime: formData.dropoffTime,
          fareCollected: parseFloat(formData.fareCollected),
          driverName: formData.driverName,
          driverId: formData.driverId,
          // LB-2 fields (notes is now actually submitted on the manual path)
          tripType: formData.tripType || 'one-way',
          additionalStops: cleanedStops,
          returnDate: formData.tripType === 'round-trip' ? (formData.returnDate || '') : '',
          returnTime: formData.tripType === 'round-trip' ? (formData.returnTime || '') : '',
          notes: formData.notes || '',
          shoppingStop: formData.shoppingStop || 'none'
        };

        const createdReceipt = await createReceipt(receiptPayload);
        
        // Update receipt with server response
        setReceipt(createdReceipt);
        setStep('success');
        
        // Reset after 5 seconds
        setTimeout(() => {
          setStep('form');
          setFormData({
            customerName: '',
            customerEmail: '',
            customerPhone: '',
            bookingReference: '',
            pickupLocation: '',
            dropoffLocation: '',
            date: new Date().toISOString().split('T')[0],
            dropoffTime: '',
            fareCollected: '',
            driverName: '',
            driverId: '',
            passengers: '',
            luggage: '',
            paymentMethod: 'cash',
            serviceType: '',
            notes: '',
            tripType: 'one-way',
            additionalStops: [],
            returnDate: '',
            returnTime: '',
            shoppingStop: 'none'
          });
          setReceipt(null);
        }, 5000);
      }
    } catch (error) {
      console.error('Failed to complete trip:', error);
      alert(`Failed to complete trip: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDownloadPDF = () => {
    downloadReceiptPDF(receipt);
  };

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-blue-50 pt-32 pb-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-12 rounded-3xl shadow-xl text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-green-400">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Receipt Issued!</h2>
            <p className="text-xl text-gray-700 mb-6">
              Receipt has been generated and emailed to the customer.
            </p>
            <div className="bg-cyan-50 p-6 rounded-xl mb-6 border border-cyan-200">
              <p className="text-gray-600 mb-2">Receipt Reference:</p>
              <p className="text-2xl font-bold text-cyan-600">{receipt?.reference}</p>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Email sent to: <strong>{receipt?.customerEmail}</strong><br />
              Admin copy sent to Stephen
            </p>
            <button
              onClick={() => navigate('/admin')}
              className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-lg font-bold hover:from-cyan-600 hover:to-blue-600 transition-all duration-300"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'preview') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-blue-50 pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Preview Receipt</h1>
            <p className="text-gray-600">Review the receipt before sending to customer</p>
          </div>

          <ReceiptTemplate receipt={receipt} />

          <div className="mt-8 flex justify-center space-x-4">
            <button
              onClick={() => setStep('form')}
              className="bg-white text-gray-700 px-6 py-3 rounded-lg font-bold border-2 border-gray-300 hover:bg-gray-50 transition-all duration-300"
            >
              Edit Details
            </button>
            <button
              onClick={handleDownloadPDF}
              className="bg-gray-700 text-white px-6 py-3 rounded-lg font-bold hover:bg-gray-800 transition-all duration-300 flex items-center space-x-2"
            >
              <Download className="w-5 h-5" />
              <span>Download PDF</span>
            </button>
            <button
              onClick={handleConfirm}
              className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-lg font-bold hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 flex items-center space-x-2"
            >
              <Mail className="w-5 h-5" />
              <span>Send Receipt</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-blue-50 pt-32 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="inline-flex items-center space-x-2 bg-coral-100 px-4 py-2 rounded-full mb-4 border border-coral-200">
            <FileText className="w-5 h-5 text-coral-600" />
            <span className="text-coral-700 font-semibold">Trip Completion</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Complete Trip & Issue Receipt
          </h1>
          <p className="text-xl text-gray-600">
            Enter trip details to generate a professional receipt for the customer
          </p>
        </div>

        {/* Form */}
        <div className="bg-white p-8 rounded-3xl shadow-xl border border-cyan-100">
          <form onSubmit={handlePreview} className="space-y-8">
            
            {/* Booking Reference Lookup */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-blue-600" />
                Load From Existing Booking
              </h3>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={bookingReference}
                  onChange={(e) => setBookingReference(e.target.value)}
                  placeholder="Enter Booking Reference Number"
                  className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={handleLoadBooking}
                  disabled={loadingBooking}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-300 whitespace-nowrap"
                >
                  {loadingBooking ? 'Loading...' : 'Load Booking'}
                </button>
              </div>
              {bookingError && (
                <p className="text-red-600 text-sm mt-2 font-semibold">{bookingError}</p>
              )}
              <p className="text-gray-600 text-sm mt-2">
                Enter a booking confirmation number to auto-fill the form below
              </p>
            </div>

            {/* Customer Information */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Customer Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Customer Name *
                  </label>
                  <input
                    type="text"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Customer Email *
                  </label>
                  <input
                    type="email"
                    name="customerEmail"
                    value={formData.customerEmail}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                    placeholder="john@example.com"
                  />
                </div>
              </div>
            </div>

            {/* Trip Details */}
            <div className="border-t border-gray-200 pt-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Trip Details</h2>
              <div className="space-y-6">
                {/* LB-2: Trip Type Selector */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Trip Type *</label>
                  <div className="flex flex-wrap gap-2" data-testid="trip-type-selector">
                    {[
                      { v: 'one-way', label: 'One-Way' },
                      { v: 'multi-stop', label: 'Multi-Stop' },
                      { v: 'round-trip', label: 'Round Trip' },
                    ].map((opt) => {
                      const active = (formData.tripType || 'one-way') === opt.v;
                      return (
                        <button
                          key={opt.v}
                          type="button"
                          data-testid={`trip-type-${opt.v}`}
                          onClick={() => setFormData((p) => ({ ...p, tripType: opt.v }))}
                          className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                            active
                              ? 'bg-cyan-600 text-white border-cyan-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-cyan-400'
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Pickup Location *
                    </label>
                    <input
                      type="text"
                      name="pickupLocation"
                      value={formData.pickupLocation}
                      onChange={handleChange}
                      required
                      data-testid="pickup-location-input"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                      placeholder="LPIA Airport, Terminal A"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Drop-off Location *
                    </label>
                    <input
                      type="text"
                      name="dropoffLocation"
                      value={formData.dropoffLocation}
                      onChange={handleChange}
                      required
                      data-testid="dropoff-location-input"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                      placeholder="Atlantis Paradise Island"
                    />
                  </div>
                </div>

                {/* LB-2: Additional Stops (visible for multi-stop and round-trip) */}
                {(formData.tripType === 'multi-stop' || formData.tripType === 'round-trip') && (
                  <div data-testid="additional-stops-section">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-gray-700 font-medium">Additional Stops</label>
                      <button
                        type="button"
                        onClick={handleAddStop}
                        data-testid="add-stop-btn"
                        className="text-sm font-medium text-cyan-700 hover:text-cyan-900"
                      >
                        + Add Stop
                      </button>
                    </div>
                    {(formData.additionalStops || []).length === 0 ? (
                      <p className="text-sm text-gray-500 italic">No additional stops added.</p>
                    ) : (
                      <div className="space-y-2">
                        {(formData.additionalStops || []).map((stop, i) => (
                          <div key={i} className="flex gap-2">
                            <input
                              type="text"
                              value={stop}
                              onChange={(e) => handleStopChange(i, e.target.value)}
                              data-testid={`stop-input-${i}`}
                              placeholder={`Stop ${i + 1} address`}
                              className="flex-1 px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveStop(i)}
                              data-testid={`remove-stop-${i}`}
                              className="px-3 py-2 text-sm text-red-600 hover:text-red-800 border border-red-200 rounded-lg hover:bg-red-50"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Trip Date *
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Drop-off Time *
                    </label>
                    <input
                      type="time"
                      name="dropoffTime"
                      value={formData.dropoffTime}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                    />
                  </div>
                </div>

                {/* LB-2: Return Date/Time (round-trip only) */}
                {formData.tripType === 'round-trip' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="return-leg-fields">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Return Date</label>
                      <input
                        type="date"
                        name="returnDate"
                        value={formData.returnDate}
                        onChange={handleChange}
                        data-testid="return-date-input"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Return Time</label>
                      <input
                        type="time"
                        name="returnTime"
                        value={formData.returnTime}
                        onChange={handleChange}
                        data-testid="return-time-input"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500"
                      />
                    </div>
                  </div>
                )}

                {/* Shopping Plaza Stop — radio (single-select, prevents double-charge) */}
                <div data-testid="shopping-plaza-stop-section">
                  <label className="block text-gray-700 font-medium mb-2">Shopping Plaza Stop</label>
                  <p className="text-xs text-gray-500 mb-3">
                    Grocery store and liquor store are in the same shopping plaza. Select one option only.
                  </p>
                  <div className="space-y-2">
                    {Object.values(SHOPPING_STOP_OPTIONS).map((opt) => (
                      <label
                        key={opt.id}
                        data-testid={`shopping-stop-option-${opt.id}`}
                        className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all duration-200 ${
                          (formData.shoppingStop || 'none') === opt.id
                            ? 'border-cyan-500 bg-cyan-50 ring-2 ring-cyan-500/20'
                            : 'border-gray-300 bg-gray-50 hover:border-cyan-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="shoppingStop"
                          value={opt.id}
                          checked={(formData.shoppingStop || 'none') === opt.id}
                          onChange={handleChange}
                          className="mt-1 accent-cyan-600"
                        />
                        <span className="text-sm text-gray-800">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Payment & Driver */}
            <div className="border-t border-gray-200 pt-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Payment & Driver</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Fare Collected (BSD) *
                  </label>
                  <input
                    type="number"
                    name="fareCollected"
                    value={formData.fareCollected}
                    onChange={handleChange}
                    required
                    min="0"
                    step="0.01"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                    placeholder="45.00"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Driver Name *
                  </label>
                  <input
                    type="text"
                    name="driverName"
                    value={formData.driverName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                    placeholder="Stephen"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Driver ID
                  </label>
                  <input
                    type="text"
                    name="driverId"
                    value={formData.driverId}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                    placeholder="DRV-001"
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-end space-x-4 pt-6">
              <button
                type="button"
                onClick={() => navigate('/admin')}
                className="bg-white text-gray-700 px-6 py-3 rounded-lg font-bold border-2 border-gray-300 hover:bg-gray-50 transition-all duration-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-8 py-3 rounded-lg font-bold hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 transform hover:scale-105 flex items-center space-x-2"
              >
                <FileText className="w-5 h-5" />
                <span>Generate Receipt</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TripCompletionForm;
